import type { AppDispatch, RootState } from "@/store";
import {
  resetAnalysisSummaryState,
  setAnalysisSummaryState
} from "@/store/slice/analysisSummary";
import { resetDatasetState, setDatasetState } from "@/store/slice/datasetSlice";
import {
  resetGlobalFilter,
  setGlobalFilterState
} from "@/store/slice/globalFilterSlice";
import { resetMapState, setMapState } from "@/store/slice/mapSlice";
import { resetSpatialState, setSpatialState } from "@/store/slice/spatialSlice";
import { resetWidgetState, setWidgetState } from "@/store/slice/widgetSlice";

/**
 * The manual analysis configuration. The app parks it while Agentic AI or
 * onboarding holds these slices.
 *
 * The two modes park the same six slices. Thus they share one snapshot type.
 * Each mode keeps its other data in its own slice. For example,
 * `onboardingSlice` keeps the `ui` data, the map camera and the entry route of a
 * tour.
 *
 * The fields have the type `unknown`. Thus the deep slice types stay out of this
 * file, because TS stops at its instantiation depth.
 *
 * `analysisJob` is not in the snapshot. It keeps the real BE jobs, which
 * continue after a mode change.
 */
export type AnalysisConfigSnapshot = {
  dataset: unknown;
  map: unknown;
  spatial: unknown;
  analysisSummary: unknown;
  globalFilter: unknown;
  widget: unknown;
};

/**
 * The function clears the two ids of the running analysis. That analysis can end
 * while the user is in the other mode. `analysisJob` keeps the real BE job, and
 * it is not in the snapshot. Thus an old id would keep the UI in the run state.
 */
export const buildAnalysisConfigSnapshot = (
  state: RootState
): AnalysisConfigSnapshot => ({
  dataset: state.dataset,
  map: state.map,
  spatial: {
    ...state.spatial,
    currRunningAnalysisId: "",
    locallyRunAnalysisId: ""
  },
  analysisSummary: state.analysisSummary,
  globalFilter: state.globalFilter,
  widget: state.widget
});

export const resetAnalysisConfigSlices = (dispatch: AppDispatch): void => {
  dispatch(resetDatasetState());
  dispatch(resetMapState());
  dispatch(resetSpatialState());
  dispatch(resetAnalysisSummaryState());
  dispatch(resetGlobalFilter());
  dispatch(resetWidgetState());
};

/**
 * This function does not draw the map again. An agentic mode change is a route
 * change. Thus `MapProvider` gets a new container and makes a new MapLibre
 * instance, and each painter runs again from the store when `isMapLoaded`
 * becomes true. Onboarding keeps its route. Thus it draws the map again itself
 * with `restoreOnboardingMapLayers`.
 */
export const restoreAnalysisConfigSnapshot = (
  dispatch: AppDispatch,
  snapshot: AnalysisConfigSnapshot
): void => {
  dispatch(
    setDatasetState(snapshot.dataset as Parameters<typeof setDatasetState>[0])
  );
  dispatch(setMapState(snapshot.map as Parameters<typeof setMapState>[0]));
  dispatch(
    setSpatialState(snapshot.spatial as Parameters<typeof setSpatialState>[0])
  );
  dispatch(
    setAnalysisSummaryState(
      snapshot.analysisSummary as Parameters<typeof setAnalysisSummaryState>[0]
    )
  );
  dispatch(
    setGlobalFilterState(
      snapshot.globalFilter as Parameters<typeof setGlobalFilterState>[0]
    )
  );
  dispatch(
    setWidgetState(snapshot.widget as Parameters<typeof setWidgetState>[0])
  );
};
